import { Button, Icon } from "@legal-500/legal500-design-system";
import { useEffect, useId, useRef, useState } from "react";
import { cms, CMS_IMAGE_MAX_SIZE_BYTES, CMS_IMAGE_SIZE_ERROR } from "@/api/cms.service";
import { CmsImagePreview } from "../CmsImagePreview/CmsImagePreview";
import styles from "./CmsImageUploadField.module.css";

type CmsImageUploadFieldProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  onUploadingChange?: (uploading: boolean) => void;
  cardLabel?: string;
  label?: string;
  guidance?: string;
  noun?: "photo" | "image" | "icon" | "poster";
  recommendation?: string;
  previewAlt?: string;
  optional?: boolean;
};

export const CMS_IMAGE_ACCEPT = "image/png,image/jpeg,image/gif,image/webp";

const IMAGE_FORMATS = "JPG, PNG, GIF or WebP, up to 20 MiB.";

function readImageHeader(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () =>
      reject(
        new Error("Unable to read this image. Please choose it again.", { cause: reader.error }),
      );
    reader.readAsArrayBuffer(file.slice(0, 12));
  });
}

function isSupportedImage(header: Uint8Array): boolean {
  const png = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  return (
    png.every((byte, index) => header[index] === byte) ||
    (header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff) ||
    ["GIF87a", "GIF89a"].includes(String.fromCharCode(...header.slice(0, 6))) ||
    (String.fromCharCode(...header.slice(0, 4)) === "RIFF" &&
      String.fromCharCode(...header.slice(8, 12)) === "WEBP")
  );
}

export function CmsImageUploadField({
  value,
  onChange,
  disabled = false,
  onUploadingChange,
  cardLabel,
  label,
  guidance,
  noun = "photo",
  recommendation = "Recommended: a square headshot, 400 × 400 pixels or larger, with the face centred.",
  previewAlt = "Card photo",
  optional = true,
}: CmsImageUploadFieldProps) {
  const title = label ?? `${noun.charAt(0).toUpperCase()}${noun.slice(1)}`;
  const optionalSuffix = optional ? " (optional)" : "";
  const id = useId();
  const guidanceId = `${id}-guidance`;
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = useRef(false);
  const pending = useRef(false);
  const uploadingCallback = useRef(onUploadingChange);
  uploadingCallback.current = onUploadingChange;
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      if (pending.current) uploadingCallback.current?.(false);
    };
  }, []);
  async function upload(file?: File) {
    if (!file || pending.current || disabled) return;
    if (file.size > CMS_IMAGE_MAX_SIZE_BYTES) {
      setError(CMS_IMAGE_SIZE_ERROR);
      if (input.current) input.current.value = "";
      return;
    }
    pending.current = true;
    setUploading(true);
    uploadingCallback.current?.(true);
    setError(null);
    try {
      const header = await readImageHeader(file);
      if (!active.current) return;
      if (!isSupportedImage(header)) {
        setError("Choose a JPG, PNG, GIF or WebP image.");
        return;
      }
      const result = await cms.uploadImage(file);
      if (active.current) onChange(result.url);
    } catch (err) {
      if (active.current) setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      pending.current = false;
      if (active.current) {
        setUploading(false);
        uploadingCallback.current?.(false);
        if (input.current) input.current.value = "";
      }
    }
  }
  return (
    <div className={styles.root}>
      <label className={styles.label} htmlFor={id}>
        {title}
        {optionalSuffix}
      </label>
      <p id={guidanceId} className={styles.guidance}>
        {guidance ?? `${IMAGE_FORMATS} ${recommendation}`}
      </p>
      {value ? (
        <div className={styles.selected}>
          <CmsImagePreview key={value} value={value} alt={previewAlt} />
          <div className={styles.selectedActions}>
            <Button
              type="button"
              variant="outlined"
              size="small"
              aria-label={cardLabel ? `Change ${noun} for ${cardLabel}` : undefined}
              aria-describedby={guidanceId}
              disabled={disabled || uploading}
              onClick={() => input.current?.click()}
            >
              {uploading ? "Uploading…" : `Change ${noun}`}
            </Button>
            <Button
              type="button"
              variant="icon"
              aria-label={cardLabel ? `Remove ${noun} for ${cardLabel}` : `Remove ${noun}`}
              disabled={disabled || uploading}
              onClick={() => onChange("")}
            >
              <Icon icon="cross" />
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outlined"
          aria-label={cardLabel ? `Upload ${noun} for ${cardLabel}` : undefined}
          aria-describedby={guidanceId}
          disabled={disabled || uploading}
          onClick={() => input.current?.click()}
        >
          {uploading ? "Uploading…" : `Upload ${noun}…`}
        </Button>
      )}
      <input
        id={id}
        ref={input}
        type="file"
        aria-label={cardLabel ? `${title} for ${cardLabel}${optionalSuffix}` : undefined}
        accept={CMS_IMAGE_ACCEPT}
        aria-describedby={guidanceId}
        hidden
        disabled={disabled || uploading}
        onChange={event => void upload(event.target.files?.[0])}
      />
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
